import { COLORS } from '@repo/design-system/index'
import { QIcon_chevron_down, QIcon_chevron_up, QIcon_globe } from '@repo/icons/index'
import classNames from 'classnames'
import { useMemo, useState } from 'react'
import { GTM_CLICK_EVENT, getEventUserType, sendGTMCustomClickEvent } from '../../../../../helpers/gtmHelper'
import { useChangeLocale } from '../../../../../modules/apis/common/user'
import { useEventGnbDateNew } from '../../../../../modules/apis/sanity'
import { useCurrentUser } from '../../../../../modules/context/UserContext'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import {
  LOCALE_EN,
  LOCALE_JA,
  LOCALE_KO,
  LOCALE_ZH_CHS,
  LOCALE_ZH_CHT,
  LocaleType,
} from '../../../../../modules/i18n/config'
import { s_blog, s_home, s_menu } from '../../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { STUDENT_BTN_ID } from '../../../../../modules/ids/gtm/student'
import urls from '../../../../../modules/urls'
import { sendNavigationMenuClickEvent } from '../../../helpers/NavigationHelpers'
import { NAVIGATION_BAR_HREF_TYPE } from '../../../interface/navigation.interface'
// Define types for locales
type Locale = 'ko' | 'en' | 'zh_chs' | 'zh_cht' | 'ja'

// Define a type for a menu item without sub-menu
interface MenuItemBase {
  id?: STUDENT_BTN_ID
  type?: NAVIGATION_BAR_HREF_TYPE
  name: string | any
  href?: string
  disabled?: boolean
}

// Define a type for a sub-menu item
interface SubMenuItem extends MenuItemBase {
  locale?: Locale
}

// Define a type for a menu item with optional sub-menu
interface MenuItem extends MenuItemBase {
  subMenu?: SubMenuItem[]
}

const getLangSetting = (locale) => {
  switch (locale) {
    case LOCALE_KO:
      return '한국어'
    case LOCALE_EN:
      return 'English'
    case LOCALE_ZH_CHS:
      return '中文（简体）'
    case LOCALE_ZH_CHT:
      return '中文（繁體）'
    case LOCALE_JA:
      return '日本語'
  }
}
const HeaderNavigationInnerMenu = () => {
  const router = useAppRouter()
  const { t, isLocaleKO, updateLandingLocale, locale } = useTranslation()
  const { mutate } = useChangeLocale()

  const { currentUser } = useCurrentUser()
  const [activeSubMenu, setActiveSubMenu] = useState(null)
  const [langSetting, setLangSetting] = useState(getLangSetting(locale))

  const toggleSubMenu = (menu) => {
    if (activeSubMenu === menu) {
      setActiveSubMenu(null)
    } else {
      setActiveSubMenu(menu)
    }
  }

  const onClickLanguage = (locale: LocaleType, localeStr: string) => {
    setLangSetting(localeStr)
    sendGTMCustomClickEvent(
      GTM_CLICK_EVENT.CLICK_LANDING_BUTTON,
      STUDENT_BTN_ID.CLICK_FOOTER_BUTTON,
      `언어변경 - ${localeStr}`,
      getEventUserType(currentUser),
      `${router.asPath.substring(1).split('/').join('-')}}`,
      router.asPath
    )

    if (currentUser) {
      mutate({ locale })
    } else {
      updateLandingLocale(locale)
    }
  }

  const handleOnClickMenuBtn = (id: STUDENT_BTN_ID, title: string, href: string, type: NAVIGATION_BAR_HREF_TYPE) => {
    sendNavigationMenuClickEvent(id, title, currentUser, router, null)

    if (type === NAVIGATION_BAR_HREF_TYPE.OUTBOUND) {
      window.open(href, '_blank')
    } else {
      router.push(href)
    }
  }

  const { data: promotionDataNew } = useEventGnbDateNew()

  const eventGnbData = useMemo(() => {
    if (!promotionDataNew || !Array.isArray(promotionDataNew.items)) return []
    return promotionDataNew?.items?.map((item) => ({
      id: STUDENT_BTN_ID.CLICK_NAVIGATION_MOBILE,
      type: NAVIGATION_BAR_HREF_TYPE.WITHIN,
      name: item.label,
      href: item.link,
      disabled: !isLocaleKO || !item.isPublic,
    }))
  }, [promotionDataNew])

  const menuItems: MenuItem[] = [
    {
      id: STUDENT_BTN_ID.CLICK_MENU_HOME,
      type: NAVIGATION_BAR_HREF_TYPE.WITHIN,
      name: t(s_menu.portalv2.home),
      href: urls.oneOnOne.index,
    },
    {
      id: STUDENT_BTN_ID.CLICK_MENU_MATERIALS,
      type: NAVIGATION_BAR_HREF_TYPE.WITHIN,
      name: t(s_menu.landing.lesson_materials),
      href: urls.oneOnOne.material,
    },
    {
      id: STUDENT_BTN_ID.CLICK_MENU_TUTOR,
      type: NAVIGATION_BAR_HREF_TYPE.WITHIN,
      name: t(s_menu.landing.tutor),
      href: urls.oneOnOne.tutor,
    },
    {
      id: STUDENT_BTN_ID.CLICK_MENU_PRICING,
      type: NAVIGATION_BAR_HREF_TYPE.WITHIN,
      name: t(s_menu.landing.pricing),
      href: currentUser ? urls.student.portal.pricing.index : urls.student.landing.pricing,
    },
    ...((Array.isArray(eventGnbData) ? eventGnbData : []) as any[]),
    {
      id: STUDENT_BTN_ID.CLICK_MENU_BUSINESS,
      type: NAVIGATION_BAR_HREF_TYPE.WITHIN,
      name: t(s_menu.landing.b2b),
      href: urls.b2b.index,
    },
    {
      id: STUDENT_BTN_ID.CLICK_MENU_RINGLE_GUIDE,
      type: NAVIGATION_BAR_HREF_TYPE.DROPDOWN,
      name: t(s_blog.category.ringle_guide),
      subMenu: [
        {
          name: t(s_blog.category.ringle_use),
          type: NAVIGATION_BAR_HREF_TYPE.WITHIN,
          href: urls.student.landing.blog.index + '#3',
          disabled: !isLocaleKO,
        },
        {
          name: t(s_home.review_title.subtitle.desktop),
          type: NAVIGATION_BAR_HREF_TYPE.WITHIN,
          href: urls.student.landing.blog.tags('링글 후기'),
          disabled: !isLocaleKO,
        },
        { name: t(s_blog.title.blog), type: NAVIGATION_BAR_HREF_TYPE.WITHIN, href: urls.student.landing.blog.index },
      ],
    },
    {
      id: STUDENT_BTN_ID.CLICK_MENU_AI_OPIC_PREP,
      type: NAVIGATION_BAR_HREF_TYPE.OUTBOUND,
      name: 'AI 오픽 프렙',
      href: 'https://prep.ringleplus.com/',
      disabled: !isLocaleKO,
    },
    {
      id: STUDENT_BTN_ID.CLICK_MENU_CHANGE_LANG,
      name: (
        <div className="flex flex-row gap-[4px] items-center">
          <QIcon_globe width={16} height={16} color={COLORS.GRAY500} />
          <span className=""> {t(s_menu.change_language)}</span>
        </div>
      ),
      subMenu: [
        { name: '한국어', locale: LOCALE_KO },
        { name: 'English', locale: LOCALE_EN },
        { name: '中文（简体）', locale: LOCALE_ZH_CHS },
        { name: '中文（繁體）', locale: LOCALE_ZH_CHT },
        { name: '日本語', locale: LOCALE_JA },
      ],
    },
  ]
  return (
    <div className="">
      <div>
        {menuItems.map((item, index) => {
          if (item.disabled) return
          return (
            <div
              key={index}
              onClick={() =>
                item.subMenu ? toggleSubMenu(item.id) : handleOnClickMenuBtn(item.id, item.name, item.href, item.type)
              }
              className=""
            >
              <div className="flex flex-row items-center">
                <div className=" flex flex-row items-center gap-[4px] px-[20px] py-[16px] w-full">
                  <span className="Body1-16Md">{item.name}</span>
                  {item.subMenu && (
                    <>
                      {activeSubMenu && activeSubMenu === item.id ? (
                        <div className="">
                          <QIcon_chevron_up width={16} height={16} />
                        </div>
                      ) : (
                        <div className="">
                          <QIcon_chevron_down width={16} height={16} />
                        </div>
                      )}
                    </>
                  )}
                </div>
                {item.id == STUDENT_BTN_ID.CLICK_MENU_CHANGE_LANG && (
                  <span className="flex justify-self-end whitespace-nowrap mr-[20px] text-gray-400 Body1-16Md">
                    {langSetting}
                  </span>
                )}
              </div>

              {item.subMenu && activeSubMenu === item.id && (
                <div className="Body1-16Md bg-gray-50">
                  {item.subMenu.map((subItem, subIndex) => {
                    if (subItem.disabled) return
                    if (item.id == STUDENT_BTN_ID.CLICK_MENU_CHANGE_LANG) {
                      return (
                        <div
                          key={subIndex}
                          className={classNames('px-[32px] py-[8px]', {
                            'text-purple-500': langSetting == subItem.name,
                          })}
                          onClick={() => onClickLanguage(subItem.locale, subItem.name)}
                        >
                          {subItem.name}
                        </div>
                      )
                    }

                    return (
                      <div key={subIndex} className="px-[32px] py-[8px]" onClick={() => router.push(subItem.href)}>
                        {subItem.name}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default HeaderNavigationInnerMenu
